import { useScrollIntoView } from 'src/hooks/useScrollIntoView'
import { Link as NavLinkType } from '.'
import { Box, Typography, TextField, MenuItem, Stack, Divider, Link } from '@mui/material'
import { ArrowForwardIcon, DownloadIcon } from 'src/assets/icons'
import { Fragment, useState } from 'react'
import { FileIcon } from 'src/assets/FileIcon'

type FormsProps = {
  link: NavLinkType
}

const Forms = ({ link }: FormsProps) => {
  useScrollIntoView(link)
  const states = [
    {
      name: 'New York',
      formPaths: {
        trustInfo: '/static/pdf/TRUST_INFO_&_PROCEDURES_I.pdf',
        autoDisbursement: '/static/pdf/AUTOMATIC_DISBURSMENT_FORM.pdf',
        joinderAgreement: '/static/pdf/JOINDER_AGREEMENT.pdf',
        disbursementRequest: '/static/pdf/DISBURSEMENT_REQUEST_FORM_(2.0.pdf',
        directDebitRequest: '/static/pdf/DIRECT_DEBIT_REQUEST_FORM.pdf'
      }
    },
    {
      name: 'New Jersey',
      formPaths: {
        trustInfo: '/static/pdf/TRUST_INFO_&_PROCEDURES_I.pdf',
        autoDisbursement: '/static/pdf/AUTOMATIC_DISBURSMENT_FORM.pdf',
        joinderAgreement: '/static/pdf/JOINDER_AGREEMENT.pdf',
        disbursementRequest: '/static/pdf/DISBURSEMENT_REQUEST_FORM_(2.0.pdf',
        directDebitRequest: '/static/pdf/DIRECT_DEBIT_REQUEST_FORM.pdf'
      }
    },
    {
      name: 'Florida',
      formPaths: {
        trustInfo: '/static/pdf/TRUST_INFO_&_PROCEDURES_I.pdf',
        autoDisbursement: '/static/pdf/AUTOMATIC_DISBURSMENT_FORM.pdf',
        joinderAgreement: '/static/pdf/JOINDER_AGREEMENT.pdf',
        disbursementRequest: '/static/pdf/DISBURSEMENT_REQUEST_FORM_(2.0.pdf',
        directDebitRequest: '/static/pdf/DIRECT_DEBIT_REQUEST_FORM.pdf'
      }
    },
    {
      name: 'Pennsylvania',
      formPaths: {
        trustInfo: '/static/pdf/TRUST_INFO_&_PROCEDURES_I.pdf',
        autoDisbursement: '/static/pdf/AUTOMATIC_DISBURSMENT_FORM.pdf',
        joinderAgreement: '/static/pdf/JOINDER_AGREEMENT.pdf',
        disbursementRequest: '/static/pdf/DISBURSEMENT_REQUEST_FORM_(2.0.pdf',
        directDebitRequest: '/static/pdf/DIRECT_DEBIT_REQUEST_FORM.pdf'
      }
    },
    {
      name: 'Massachusetts',
      formPaths: {
        trustInfo: '/static/pdf/TRUST_INFO_&_PROCEDURES_I.pdf',
        autoDisbursement: '/static/pdf/AUTOMATIC_DISBURSMENT_FORM.pdf',
        joinderAgreement: '/static/pdf/JOINDER_AGREEMENT.pdf',
        disbursementRequest: '/static/pdf/DISBURSEMENT_REQUEST_FORM_(2.0.pdf',
        directDebitRequest: '/static/pdf/DIRECT_DEBIT_REQUEST_FORM.pdf'
      }
    },
    {
      name: 'North Carolina',
      formPaths: {
        trustInfo: '/static/pdf/TRUST_INFO_&_PROCEDURES_I.pdf',
        autoDisbursement: '/static/pdf/AUTOMATIC_DISBURSMENT_FORM.pdf',
        joinderAgreement: '/static/pdf/JOINDER_AGREEMENT.pdf',
        disbursementRequest: '/static/pdf/DISBURSEMENT_REQUEST_FORM_(2.0.pdf',
        directDebitRequest: '/static/pdf/DIRECT_DEBIT_REQUEST_FORM.pdf'
      }
    }
  ] as const
  const [selectedState, setSelectedState] = useState<(typeof states)[number] | undefined>(states[0])

  const forms = [
    {
      id: 'trustInfo',
      label: 'Trust Information and Procedures',
      href: selectedState?.formPaths.trustInfo
    },
    {
      id: 'autoDisbursement',
      label: 'Automatic Disbursement Form',
      href: selectedState?.formPaths.autoDisbursement
    },
    {
      id: 'joinderAgreement',
      label: 'Joinder Agreement',
      href: selectedState?.formPaths.joinderAgreement
    },
    {
      id: 'disbursementRequest',
      label: 'Disbursement Request Form',
      href: selectedState?.formPaths.disbursementRequest
    },
    {
      id: 'directDebitRequest',
      label: 'Direct Debit Request Form',
      href: selectedState?.formPaths.directDebitRequest
    }
  ]

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const state = states.find((state) => state.name === e.target.value)
    setSelectedState(state)
  }

  return (
    <Box ref={link.componentRef} className="px-28 pb-12 ltb_1300:px-12 tbmd:mx-5 tbmd:px-0">
      <Box className="flex items-center gap-4">
        <Box className="flex h-10 w-10 items-center justify-center rounded-full bg-[#E4F2F7]">
          <ArrowForwardIcon fontSize="small" className="text-[#4E96C4]" />
        </Box>
        <Typography className="font-[Barlow] text-[40px] font-bold text-[#3C78A4]">Forms</Typography>
      </Box>
      <Typography className="my-4 text-[#202939] text_lg_normal">
        Please select the state in which you are applying to download the correct form.
      </Typography>
      <Box className="mb-8 flex items-center gap-3">
        <Typography className="text-[#4B5565]">Select State:</Typography>
        <TextField
          sx={{ '& .MuiSelect-outlined': { py: '8px !important' } }}
          value={selectedState?.name || ''}
          select
          className="w-[185px]"
          onChange={handleChange}
        >
          {states.map((state) => (
            <MenuItem value={state.name} key={state.name}>
              {state.name}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Stack>
        {forms.map((form) => (
          <Fragment key={form.id}>
            <Link
              target="_blank"
              underline="hover"
              referrerPolicy="no-referrer"
              href={form.href}
              className="flex items-center justify-between p-4 hover:bg-white"
            >
              <Box className="flex items-center gap-2">
                <FileIcon />
                <Typography className="text-[#4B5565] no-underline text_lg_normal">{form.label}</Typography>
              </Box>
              <Link href={form.href} download className="flex rounded-xl bg-[#BBDEEB] p-2 text-center">
                <DownloadIcon className="w-[21px] text-[#346791]" />
              </Link>
            </Link>
            <Divider className="mb-4 bg-[#E3E8EF]" />
          </Fragment>
        ))}
      </Stack>
    </Box>
  )
}

export default Forms
